// utils.ts
import dayjs, { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { AssetType, IActivity, IMagicSuggestion } from '../redux/activity/activitySlice';
import { ICompletedActivities, ICompletedActivity } from '../redux/client/clientSlice';
import {
  ACTIVITY_LIBRARY_ROUTE,
  APPLICATIONS_ROUTE,
  APPOINTMENTS_ROUTE,
  ARCHIVED_CLIENTS_ROUTE,
  ARCHIVED_COACHES_ROUTE,
  CALENDAR_SETTINGS_ROUTE,
  CHAT_ROUTE,
  CHECKOUT_PAGE_ROUTE,
  CLIENTS_ROUTE,
  COACHES_ROUTE,
  HOME_ROUTE,
  NOTIFICATION_ROUTE,
  PAYMENT_INFO_ROUTE,
  PAYMENT_SETTINGS_ROUTE,
  PAYMENT_SUCCESS_ROUTE,
  SETTINGS_ROUTE,
  SUBSCRIPTION_PLANS,
  USER_GROUPS_ROUTE,
  VIEW_BOOKINGS_ROUTE,
  MANAGE_TEAM_ROUTE,
  TEAM_CLIENTS_ROUTE,
} from './routes';
import i18n from '../i18n';
import { videoDontShowAgainKey } from '../config/constants';
import { IClientWithTotalClientCount } from '../redux/client/clientSaga';
import { IGetAllCoachesData } from '../redux/coach/coachSaga';
import { AdvanceActivityStepType } from '../types/activity';
import { ActivityLibraries } from '../redux/activityLibrary/activityLibrarySlice';
import { DayOfWeekEnum } from '../types/booking';
import { SubscriptionPlanTypes } from '../types/coach';
import { IGetNoteSuggestions, IProcessedAIResponse } from '../redux/note/noteSlice';

export const getGoodEngagement = () => ({
  data: Array.from({ length: 14 }, (_, i) => ({
    id: i + 1,
    data: [2, 1, 2, 1, 2, 1, 2, 2, 1][i] || 2,
  })),
  percentage: '90%',
});

export const getMediumEngagement = () => ({
  data: Array.from({ length: 14 }, (_, i) => ({
    id: i + 1,
    data: [1, 0, 0, 0, 1, 0, 1, 0, 1, 0, 0, 1, 0][i] || 0,
  })),
  percentage: '20%',
});

export const randomEngagement = (level: string) =>
  level === 'good' ? getGoodEngagement() : getMediumEngagement();

export const getGreeting = () => {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 12) return i18n.t('common.greeting.morning');
  if (hour >= 12 && hour < 17) return i18n.t('common.greeting.afternoon');
  if (hour >= 17 && hour < 22) return i18n.t('common.greeting.evening');
  return i18n.t('common.greeting.night');
};

export const getTitle = (firstPathPart: string) => {
  switch (firstPathPart) {
    case HOME_ROUTE:
      return getGreeting();
    case CLIENTS_ROUTE:
      return i18n.t('common.modules.activeClients');
    case COACHES_ROUTE:
      return i18n.t('common.modules.activeCoaches');
    case APPOINTMENTS_ROUTE:
      return i18n.t('common.modules.appointments');
    case ACTIVITY_LIBRARY_ROUTE:
      return i18n.t('common.modules.activityLibrary');
    case CHAT_ROUTE:
      return i18n.t('common.modules.chat');
    case SETTINGS_ROUTE:
      return i18n.t('common.modules.settings');
    case NOTIFICATION_ROUTE:
      return i18n.t('common.modules.notifications');
    case APPLICATIONS_ROUTE:
      return i18n.t('common.modules.applications');
    case ARCHIVED_CLIENTS_ROUTE:
      return i18n.t('common.modules.archivedClients');
    case ARCHIVED_COACHES_ROUTE:
      return i18n.t('common.modules.archivedCoaches');
    case USER_GROUPS_ROUTE:
      return i18n.t('common.modules.userGroups');
    case PAYMENT_INFO_ROUTE:
      return i18n.t('common.modules.paymentInfo');
    case PAYMENT_SUCCESS_ROUTE:
      return i18n.t('common.modules.paymentSuccess');
    case PAYMENT_SETTINGS_ROUTE:
      return i18n.t('common.modules.paymentSettings');
    case SUBSCRIPTION_PLANS:
      return i18n.t('common.modules.subscriptionPlans');
    case VIEW_BOOKINGS_ROUTE:
      return i18n.t('common.management.bookings');
    case CHECKOUT_PAGE_ROUTE:
      return i18n.t('subscriptionPlan.subscription');
    case CALENDAR_SETTINGS_ROUTE:
      return i18n.t('common.modules.bookingSettings');
    case MANAGE_TEAM_ROUTE:
      return i18n.t('common.modules.team');
    case TEAM_CLIENTS_ROUTE:
      return i18n.t('common.modules.team');
    default:
      return '';
  }
};
export const transformClientServerData = (
  data: IClientWithTotalClientCount
): IClientWithTotalClientCount | {} => {
  if (!data) return {};

  const clients = data.clients?.map((client) => ({
    id: client.client.id,
    name: client.client.firstName,
    firstName: client.client.firstName,
    lastName: client.client.lastName,
    email: client.client.email,
    surname: client.client.surname,
    profilePic: client.client.profilePic,
    objectives: client.client.objectives,
    team: client.client.teamMembers,
    objectivesString: Array.isArray(client.client.objectives)
      ? client.client.objectives.join(', ')
      : 'No objectives',
    lastActivity: client.client.lastActivity ? client.client.lastActivity : '8.00AM Yesterday',
    engagementLevel: client.engagementLevel.engagementLevels,
    isActive: client.client.isActive,
    updatedAt: client.client.updatedAt,
    clientType: client.client.clientType,
  }));

  return {
    clients,
    totalClientCount: data.totalClientCount,
    paymentStatus: data.paymentStatus,
    clientCountStatus: data.clientCountStatus,
  };
};

// Transfer magic suggestion data from server to UI
export const transformActivities = (activities: IMagicSuggestion[]): IActivity[] => {
  const baseImageURL = '/assets/images/mgs-';

  return activities.map((activity, idx) => {
    const activityId = (idx + 1).toString();
    const steps = activity.steps.map((step, stepIdx) => ({
      id: (stepIdx + 1).toString(),
      activityId,
      description: '',
      stepOrder: stepIdx + 1,
      text: step,
      asset: '',
      assetType: AssetType.TEXT,
      createdAt: '',
      updatedAt: '',
      contentType: '',
      assetFile: null,
      isAdvanceStep: false,
    }));
    const advanceSteps = activity.advance_activity_tracking_steps.map((step, stepIdx) => {
      const text = step.replace(/\([^)]*\)/g, '').trim();
      const regexBrackets = /\((.*?)\)/;
      const regexSelection = /SELECTION:\s*(.+)/;
      const match = regexBrackets.exec(step);
      let advanceStepType = AdvanceActivityStepType.ANY;
      let advanceStepData: string[] = [];
      if (match) {
        const content = match[1];
        if (content === 'YES/NO') {
          advanceStepType = AdvanceActivityStepType.BOOLEAN;
        } else if (content === AdvanceActivityStepType.NUMBER) {
          advanceStepType = AdvanceActivityStepType.NUMBER;
        } else if (regexSelection.test(content)) {
          const selectionMatch = regexSelection.exec(content);
          if (selectionMatch) {
            advanceStepType = AdvanceActivityStepType.SELECTION;
            advanceStepData = selectionMatch[1].split(',').map((option) => option.trim());
          }
        } else if (content === AdvanceActivityStepType.PERCENTAGE) {
          advanceStepType = AdvanceActivityStepType.PERCENTAGE;
        }
      }
      return {
        id: (steps.length + stepIdx).toString(),
        activityId,
        stepOrder: steps.length + stepIdx + 1,
        text,
        description: '',
        asset: '',
        assetType: AssetType.TEXT,
        createdAt: '',
        updatedAt: '',
        contentType: '',
        assetFile: null,
        isAdvanceStep: true,
        advanceStepType,
        advanceStepData,
      };
    });

    const activitySteps = steps.concat(advanceSteps);

    return {
      id: activityId,
      coverImage: `${baseImageURL}${activityId}.png`,
      name: activity.title,
      objective: activity.summary,
      ActivityStep: activitySteps,
      frequency: activity.frequency,
      situation: activity.situation,
    };
  });
};

export const categorizeActivities = (activities: ICompletedActivity[]): ICompletedActivities => {
  const today = dayjs().startOf('day');
  const yesterday = dayjs().subtract(1, 'day').startOf('day');

  const categorized: ICompletedActivities = {
    today: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    yesterday: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    last7Days: {
      activity: [],
      totalCompletedActivityCount: 0,
    },
    earlier: {
      activity: [],
      from: '',
      to: '',
    },
    all: [],
  };

  activities.forEach((activity) => {
    const completedDate = dayjs(activity.completedDate);
    categorized.all.push(activity);
    if (completedDate.isSame(today, 'day')) {
      categorized.today.activity.push(activity);
    } else if (completedDate.isSame(yesterday, 'day')) {
      categorized.yesterday.activity.push(activity);
    } else if (
      completedDate.isAfter(yesterday.subtract(6, 'day')) &&
      completedDate.isBefore(today)
    ) {
      categorized.last7Days.activity.push(activity);
    } else {
      categorized.earlier.activity.push(activity);
    }
  });
  const sortByCompletedDate = (a: ICompletedActivity, b: ICompletedActivity) =>
    dayjs(b.completedDate).diff(dayjs(a.completedDate));
  // Sorting each category

  categorized.today.activity.sort(sortByCompletedDate);
  categorized.yesterday.activity.sort(sortByCompletedDate);
  categorized.last7Days.activity.sort(sortByCompletedDate);
  categorized.earlier.activity.sort(sortByCompletedDate);

  // Update totalCompletedActivityCount for each category
  categorized.today.totalCompletedActivityCount = categorized.today.activity.length;
  categorized.yesterday.totalCompletedActivityCount = categorized.yesterday.activity.length;
  categorized.last7Days.totalCompletedActivityCount = categorized.last7Days.activity.length;

  return categorized;
};

export const getShowVideoDemoStatus = (): string | null =>
  localStorage.getItem(videoDontShowAgainKey);

export const isInteger = (num: string) => /^-?[0-9]+$/.test(`${num}`);

export const transformCoachesServerData = (data: IGetAllCoachesData): IGetAllCoachesData | {} => {
  if (!data) return {};

  const coaches = data.coaches?.map((coach) => ({
    id: coach.id,
    name: coach.firstName,
    firstName: coach.firstName,
    lastName: coach.lastName,
    email: coach.email,
    surname: coach.surname,
    profilePic: coach.profilePic,
    tp: coach.tp,
    clientCount: coach.clientCount,
    status: coach.status,
    country: coach.country,
    isActive: coach.isActive,
    archived: coach.archived,
    activeClients: coach.activeClients,
    inactiveClients: coach.inactiveClients,
    archivedClients: coach.archivedClients,
    brandTag: coach.brandTag,
    lastSeenTime: coach.lastSeanTime,
    subscriptionPlan: coach.subscriptionPlanName,
    coachTypes: coach.coachTypes?.map((type) => type.coachType),
    therapyTypes: coach.coachTypes?.map((e) => e.description),
  }));
  return { coaches, totalCoachesCount: data.totalClientCount };
};

export const toastStyles = {
  success: {
    style: {
      // Background color
      background:
        'var(--Alert-Japanese-Laurel, linear-gradient(175deg, #57B15A -9.81%, #2F7D32 96.32%))',
      color: '#FFFFFF', // Text color
      borderRadius: 10,
      fontFamily: 'Satoshi',
      fontSize: 15,
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '20px',
    },
    iconTheme: {
      primary: '#FFFFFF', // Icon background color
      secondary: '#2F7D32', // Icon color
    },
  },
  error: {
    style: {
      // Background color
      background:
        'var(--Alert-Sunset-Orange, linear-gradient(175deg, #FF9A9A -9.81%, #FF5858 96.32%))',
      color: '#FFFFFF', // Text color
      borderRadius: 10,
      fontFamily: 'Satoshi',
      fontSize: 15,
      fontStyle: 'normal',
      fontWeight: 700,
      lineHeight: '20px',
    },
    iconTheme: {
      primary: '#FFFFFF', // Icon background color
      secondary: '#FF5858', // Icon color
    },
  },
};

export const transformEditActivityData = (activity: ActivityLibraries): IActivity => ({
  id: activity.id,
  name: activity.name,
  objective: activity.objective,
  ActivityStep: activity.ActivityLibraryStep,
});

export function displayLongSentences(sentence: string, maximumCharcterCount: number): string {
  if (sentence.length > maximumCharcterCount) {
    return `${sentence.substring(0, maximumCharcterCount)}...`;
  }
  return sentence;
}

export function getDaySuffix(day: number, language: string) {
  if (language === 'ar') {
    return 'يوم';
  }
  if (language === 'pt') {
    if (day > 3 && day < 21) return 'º';
    if (day % 10) {
      return 'º';
    }
  } else {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }
}

export function formatDate(dateString: string, language: string) {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString(language, { month: 'short' });
  const year = date.getFullYear();
  const suffix = getDaySuffix(day, language);

  return `${day}${suffix} ${month} ${year}`;
}

export function getCoachUnavailableDates(availabilities) {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const allDaysOfWeek = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
  ];
  const availableDaysOfWeek = availabilities.map((availability) => availability.dayOfWeek);
  const disabledDaysOfWeek = allDaysOfWeek.filter((day) => !availableDaysOfWeek.includes(day));

  let currentDate = dayjs();
  const unavailableDates: Dayjs[] = [];

  if (disabledDaysOfWeek.length > 0) {
    while (unavailableDates.length < 365 && dayjs().add(2, 'years').isAfter(currentDate)) {
      if (disabledDaysOfWeek.includes(currentDate.format('dddd').toUpperCase())) {
        unavailableDates.push(currentDate);
      }
      currentDate = currentDate.add(1, 'day');
    }
  }
  return unavailableDates;
}

export function formatDay(day: number): DayOfWeekEnum {
  switch (day) {
    case 0:
      return DayOfWeekEnum.SUNDAY;
    case 1:
      return DayOfWeekEnum.MONDAY;
    case 2:
      return DayOfWeekEnum.TUESDAY;
    case 3:
      return DayOfWeekEnum.WEDNESDAY;
    case 4:
      return DayOfWeekEnum.THURSDAY;
    case 5:
      return DayOfWeekEnum.FRIDAY;
    case 6:
      return DayOfWeekEnum.SATURDAY;
    default:
      throw new Error('Invalid day of week');
  }
}

export function formatTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = mins < 10 ? `0${mins}` : mins;

  return `${formattedHours}:${formattedMinutes} ${period}`;
}

export function formatDateObject(dateObject) {
  const dateString = dateObject.$d; // Assuming the date string property name is consistent

  if (!dateString) {
    throw new Error('Missing date string property in the provided object.');
  }

  const dateObj = new Date(dateString);

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0'); // Add leading zero for single-digit months
  const day = String(dateObj.getDate()).padStart(2, '0'); // Add leading zero for single-digit days

  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  const seconds = String(dateObj.getSeconds()).padStart(2, '0');
  const milliseconds = String(dateObj.getMilliseconds()).padStart(3, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
}

export function formatDuration(minutes: number): string {
  const { language } = i18n;
  const getTranslation = (singularKey: string, pluralKey: string, count: number) =>
    count === 1 ? i18n.t(singularKey) : i18n.t(pluralKey);

  if (minutes < 60) {
    const minuteLabel = getTranslation(
      'bookings.duration.minute',
      'bookings.duration.minutes',
      minutes
    );
    if (language === 'si') {
      return `${minuteLabel} ${minutes}`;
    }
    return `${minutes} ${minuteLabel}`;
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const hourLabel = getTranslation('bookings.duration.hour', 'bookings.duration.hours', hours);
  const minuteLabel =
    remainingMinutes > 0
      ? getTranslation('bookings.duration.minute', 'bookings.duration.minutes', remainingMinutes)
      : '';
  if (language === 'si') {
    return `${hourLabel} ${hours}${
      remainingMinutes > 0 ? `, ${minuteLabel} ${remainingMinutes}` : ''
    }`;
  }
  return `${hours} ${hourLabel}${
    remainingMinutes > 0 ? `, ${remainingMinutes} ${minuteLabel}` : ''
  }`;
}

export function formatPhoneNumber(phoneNumber: string): string {
  return phoneNumber;
}

export const formatAddress = (address: string): string => {
  if (!address) return '';

  // Split the address into parts by commas, spaces, or new lines, trim and capitalize each part
  const parts = address
    .split(/\s+|,|\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1));

  let formattedAddress = '';
  let currentLine = '';

  parts.forEach((part) => {
    if (currentLine.length + part.length + (currentLine ? 2 : 0) > 50) {
      formattedAddress += (formattedAddress ? '\n' : '') + currentLine;
      currentLine = part;
    } else {
      currentLine += (currentLine ? ', ' : '') + part;
    }
  });

  if (currentLine) {
    formattedAddress += (formattedAddress ? '\n' : '') + currentLine;
  }

  return formattedAddress;
};

export const truncateFileName = (fileName: string, maxLength: number): string => {
  const ext = fileName.split('.').pop() ?? '';
  const name = fileName.slice(0, fileName.length - ext.length - 1);

  if (fileName.length <= maxLength) {
    return fileName;
  }

  const charsToShow = maxLength - ext.length - 3;
  const frontChars = Math.floor(charsToShow / 2);
  const backChars = charsToShow - frontChars;

  return `${name.slice(0, frontChars)}...${name.slice(-backChars)}.${ext}`;
};

export const formatSubscriptionPlanName = (plan: SubscriptionPlanTypes): string => {
  if (plan === SubscriptionPlanTypes.NONE) {
    return '-';
  }
  return plan.split('_').join(' ').toLowerCase();
};

export const coachTypesMapper = (coachTypes?: string[] | null) => {
  if (!Array.isArray(coachTypes)) {
    return '-';
  }
  const translatedTypes: string[] = [];
  coachTypes.forEach((type) => {
    if (!type) return;
    translatedTypes.push(t(`coachTypes.${type}`));
  });
  if (!translatedTypes.length) {
    return '-';
  }
  return translatedTypes.join(', ');
};

export const coachTypesDescriptionMapper = (therapyTypes?: string[] | null) => {
  if (!Array.isArray(therapyTypes)) {
    return '-';
  }
  const therapyTypesFlat = therapyTypes.flat();
  const translatedTypes: string[] = [];
  therapyTypesFlat.forEach((type) => {
    if (!type) return;
    translatedTypes.push(t(`therapyTypesFlat.${type}`));
  });
  if (!translatedTypes.length) {
    return '-';
  }
  return translatedTypes.join(', ');
};

export function convertNoteSuggestions(
  apiResponse: IGetNoteSuggestions | null | undefined
): IProcessedAIResponse[] {
  if (!apiResponse || !Array.isArray(apiResponse.getNoteSuggestions)) {
    return [];
  }

  const suggestionMap: { [key: string]: IProcessedAIResponse } = {};

  for (let i = 0; i < apiResponse.getNoteSuggestions.length; i += 1) {
    const noteSuggestion = apiResponse.getNoteSuggestions[i];
    const { id, type, suggestion } = noteSuggestion;

    if (type && suggestion) {
      const existingSuggestion = suggestionMap[type];
      if (existingSuggestion) {
        if (!existingSuggestion.data?.length) existingSuggestion.data = [];
        existingSuggestion.data.push({ id, value: suggestion });
        suggestionMap[type] = existingSuggestion;
      } else {
        suggestionMap[type] = {
          title: type,
          data: [{ id, value: suggestion }],
        };
      }
    }
  }
  return Object.values(suggestionMap);
}

export const formatRoleForDisplay = (role) => {
  if (!role) return '';
  return role
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatRole = (role) => role.toUpperCase().replace(/\s/g, '_');

export const objectivesRegex = /^(?=.*[a-zA-Z])[ \u00C0-\u017Fa-zA-Z()-/&?.]+$/;

export const isValidObjective = (objective: string): boolean => objectivesRegex.test(objective);
